import { memoize } from "lodash";
import getCoreUIStore from "../../core/core-ui-store";
import { computed, observable, runInAction } from "mobx";
import { TWebPaymentsObject, TWebPaymentsSearchOptions } from "../../../Types";
import getCorePopUpStore from "../../core/core-popup-store";
import { apiCallRefundWebSubscription } from "../../../api/auditor-api/api-call-refund-web-payments-table";
import {
  isDefinedArray,
  isDefinedString,
  isNullOrUndefined,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import i18n from "../../../constants/i18n";
import { getSpecificDateWithDays } from "../../../core/et3-date";
import { apiCallGetWebPaymentsTableByDate } from "../../../api/auditor-api/api-call-get-web-payments-table-by-date";
import { apiCallGetWebPaymentsTableByUserID } from "../../../api/auditor-api/api-call-get-web-payments-table-by-userid";
import { isDateRangeValid } from "../../../helpers/et3-date-helper";
import { apiCallGetWebPaymentsTableByUserEmail } from "../../../api/auditor-api/api-call-get-web-payments-table-by-email";
import { apiCallGetWebPaymentsTableByTransactionID } from "../../../api/auditor-api/api-call-get-web-payments-table-by-transaction-id";
import { apiCallCancelWebSubscription } from "../../../api/auditor-api/api-call-cancel-web-payments-table";
import { EHyperpayPaymentStatus } from "../../../constants/enum/hyperpay-payment-status";

class webPayments {
  webPaymentArray = observable.box<TWebPaymentsObject[]>([]);
  webPaymentChoiceValue =
    observable.box<TWebPaymentsSearchOptions>("BY_USER_ID");
  webPaymentsUserID = observable.box<number>(0);
  webPaymentsEmail = observable.box<string>("");
  webPaymentsTransactionID = observable.box<string>("");
  cancelWebPaymentObject = observable.box<{
    userID: number;
    transactionID: string;
    paymentColumnStatus: EHyperpayPaymentStatus;
  }>({
    userID: -1,
    transactionID: "",
    paymentColumnStatus: EHyperpayPaymentStatus.SUBSCRIPTION_CANCELLED
  });
  refundWebPaymentObject = observable.box<{
    userID: number;
    transactionID: string;
  }>({
    userID: -1,
    transactionID: ""
  });

  isWebPaymentAlreayRefunded = (referenceID: string) => {
    const webPaymentArray = this.webPaymentArray.get();
    const newWebPaymentArray = webPaymentArray.filter((payment) => {
      return (
        payment.referenceID === referenceID &&
        payment.refundOrCancel.paymentColumnStatus ===
          EHyperpayPaymentStatus.SUBSCRIPTION_REFUNDED
      );
    });
    return isDefinedArray(newWebPaymentArray);
  };

  fromDate = observable.box<Date>(
    getSpecificDateWithDays({
      daysSubtracted: 0,
      daysAdded: 0
    })
  );
  toDate = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );

  isGetWebPaymentDisabled = computed(() => {
    const webPaymentChoiceValue = getWebPayments().webPaymentChoiceValue.get();
    const webPaymentsUserID = getWebPayments().webPaymentsUserID.get();
    const webPaymentsEmail = getWebPayments().webPaymentsEmail.get();
    const webPaymentsTransactionID =
      getWebPayments().webPaymentsTransactionID.get();
    return webPaymentChoiceValue === "BY_USER_ID"
      ? isPositiveNumber(webPaymentsUserID)
      : webPaymentChoiceValue === "BY_EMAIL"
        ? isDefinedString(webPaymentsEmail)
        : webPaymentChoiceValue === "BY_TRANSACTION_ID"
          ? isDefinedString(webPaymentsTransactionID)
          : !isNullOrUndefined(this.toDate.get()) &&
            !isNullOrUndefined(this.toDate.get());
  });

  setWebPaymentChoice = (value: TWebPaymentsSearchOptions) => {
    if (value === "BY_DATE") {
      this.webPaymentsUserID.set(0);
      this.webPaymentsEmail.set("");
      this.webPaymentsTransactionID.set("");
    }
    getWebPayments().webPaymentChoiceValue.set(value);
  };

  setWebPaymentEmail = (email: string) => {
    runInAction(() => {
      this.webPaymentsEmail.set(email);
    });
  };

  setWebPaymentTransactionID = (transactionID: string) => {
    runInAction(() => {
      this.webPaymentsTransactionID.set(transactionID);
    });
  };

  getWebPaymentsTable = async () => {
    const webPaymentChoiceValue = this.webPaymentChoiceValue.get();
    if (
      !isDateRangeValid({
        fromDate: this.fromDate.get(),
        toDate: this.toDate.get(),
        isLimitedToOneMonth: true
      })
    ) {
      return;
    }
    getCoreUIStore().setIsLoading("AUDITOR_WEB_PAYMENTS", true);
    const { data, status, code } =
      webPaymentChoiceValue === "BY_USER_ID"
        ? await apiCallGetWebPaymentsTableByUserID({
            userID: this.webPaymentsUserID.get()
          })
        : webPaymentChoiceValue === "BY_EMAIL"
          ? await apiCallGetWebPaymentsTableByUserEmail({
              userEmail: this.webPaymentsEmail.get()
            })
          : webPaymentChoiceValue === "BY_TRANSACTION_ID"
            ? await apiCallGetWebPaymentsTableByTransactionID({
                transactionID: this.webPaymentsTransactionID.get()
              })
            : await apiCallGetWebPaymentsTableByDate({
                dateMin: this.fromDate.get(),
                dateMax: this.toDate.get()
              });
    getCoreUIStore().setIsLoading("AUDITOR_WEB_PAYMENTS", false);
    if (status === "OK") {
      this.webPaymentArray.set(data);
    } else {
      console.log("code ", code);
      console.log("status ", status);
      getCorePopUpStore().showAlertMessage(code);
    }
    return;
  };

  setWebPaymentCancelObject = (
    paymentColumnStatusCancelled: boolean,
    userID: number,
    transactionID: string,
    paymentColumnStatus: EHyperpayPaymentStatus
  ) => {
    if (paymentColumnStatusCancelled) {
      return;
    }
    getWebPayments().cancelWebPaymentObject.set({
      userID,
      transactionID,
      paymentColumnStatus
    });
    getCorePopUpStore().openConfirmationPopUps("WEB_PAYMENT_CANCEL_POPUP");
  };

  setWebPaymentRefundObject = (
    isWebPaymentAlreayRefunded: boolean,
    userID: number,
    transactionID: string
  ) => {
    getWebPayments().refundWebPaymentObject.set({
      userID,
      transactionID
    });
    isWebPaymentAlreayRefunded
      ? getCorePopUpStore().openConfirmationPopUps(
          "WEB_PAYMENT_PREVIOUS_REFUND_POPUP"
        )
      : getCorePopUpStore().openConfirmationPopUps("WEB_PAYMENT_REFUND_POPUP");
  };

  cancelWebPaymentsTable = async (
    userID: number,
    transactionID: string,
    paymentColumnStatus: EHyperpayPaymentStatus
  ) => {
    getCorePopUpStore().closeConfirmationPopUps();
    if (
      paymentColumnStatus === EHyperpayPaymentStatus.SUBSCRIPTION_CANCELLED ||
      paymentColumnStatus === EHyperpayPaymentStatus.SUBSCRIPTION_EXPIRED
    ) {
      getCorePopUpStore().showAlertMessage("ALREADY_CANCELLED");
      return;
    }
    getCoreUIStore().setIsLoading("AUDITOR_WEB_PAYMENTS", true);
    const { status, code, data } = await apiCallCancelWebSubscription(
      userID,
      transactionID
    );
    getCoreUIStore().setIsLoading("AUDITOR_WEB_PAYMENTS", false);
    //@ts-ignore
    const dataErrorString = reduce(() => data.data, "");
    if (status === "OK") {
      const webPaymentArray = this.webPaymentArray.get();
      const newWebPaymentArray = webPaymentArray.map((payment) => {
        if (payment.referenceID === transactionID) {
          payment.paymentStatus = EHyperpayPaymentStatus.SUBSCRIPTION_CANCELLED;
          payment.refundOrCancel.paymentColumnStatus =
            EHyperpayPaymentStatus.SUBSCRIPTION_CANCELLED;
        }
        return payment;
      });
      this.webPaymentArray.set(newWebPaymentArray);
      getCorePopUpStore().showSuccessMessage("SUCCESS");
    } else if (isDefinedString(dataErrorString) && status !== "OK") {
      const webPaymentErrorMessage = `${i18n.get(code)} : (${dataErrorString})`;
      //@ts-ignore
      getCorePopUpStore().showAlertMessage(webPaymentErrorMessage);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
    return;
  };

  refundWebPaymentsTable = async (userID: number, transactionID: string) => {
    getCorePopUpStore().closeConfirmationPopUps();
    getCoreUIStore().setIsLoading("AUDITOR_WEB_PAYMENTS", true);
    const { status, code, data } = await apiCallRefundWebSubscription(
      userID,
      transactionID
    );
    getCoreUIStore().setIsLoading("AUDITOR_WEB_PAYMENTS", false);
    //@ts-ignore
    const dataErrorString = reduce(() => data.data, "");
    if (status === "OK") {
      const webPaymentArray = this.webPaymentArray.get();
      const newWebPaymentArray = webPaymentArray.map((payment) => {
        if (payment.referenceID === transactionID) {
          payment.paymentStatus = EHyperpayPaymentStatus.SUBSCRIPTION_REFUNDED;
          payment.refundOrCancel.paymentColumnStatus =
            EHyperpayPaymentStatus.SUBSCRIPTION_REFUNDED;
        }
        return payment;
      });
      this.webPaymentArray.set(newWebPaymentArray);
      getCorePopUpStore().showSuccessMessage("SUCCESS");
    } else if (isDefinedString(dataErrorString) && status !== "OK") {
      const webPaymentErrorMessage = `${i18n.get(code)} : (${dataErrorString})`;
      //@ts-ignore
      getCorePopUpStore().showAlertMessage(webPaymentErrorMessage);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
    return;
  };
}

const getWebPayments = memoize(
  () => new webPayments(),
  () => 1
);

export default getWebPayments;
